/**
 * Modello tipizzato dei contenuti dinamici gestiti da BO tramite
 * fo-contenuti-statici. Ogni campo dichiara il proprio `tipo`, cosicché il FE
 * sappia sempre come renderizzarlo indipendentemente dalla chiave a cui
 * appartiene:
 * - `testo`/`html`: stringa semplice (l'html è comunque sanificato da Angular
 *   quando legato a `[innerHTML]`)
 * - `link`: URL assoluto (sito esterno o pagina interna)
 * - `immagine`/`file`: contenuto binario codificato base64 (`valore`, senza
 *   prefisso `data:`), con il relativo `mimeType`
 */

export interface ContenutoTesto {
  tipo: 'testo';
  valore: string;
}

export interface ContenutoHtml {
  tipo: 'html';
  valore: string;
}

export interface ContenutoLink {
  tipo: 'link';
  valore: string;
}

export interface ContenutoImmagine {
  tipo: 'immagine';
  valore: string;
  mimeType: string;
  nomeFile?: string;
}

export interface ContenutoFile {
  tipo: 'file';
  valore: string;
  mimeType: string;
  nomeFile: string;
}

export type ContenutoCampo =
  | ContenutoTesto
  | ContenutoHtml
  | ContenutoLink
  | ContenutoImmagine
  | ContenutoFile;

// Helper per costruire i valori di fallback lato FE senza ripetere `tipo` ad ogni campo.
export const testo = (valore: string): ContenutoTesto => ({ tipo: 'testo', valore });
export const html = (valore: string): ContenutoHtml => ({ tipo: 'html', valore });
export const link = (valore: string): ContenutoLink => ({ tipo: 'link', valore });
export const immagine = (
  valore: string,
  mimeType: string,
  nomeFile?: string,
): ContenutoImmagine => ({ tipo: 'immagine', valore, mimeType, nomeFile });
export const file = (valore: string, mimeType: string, nomeFile: string): ContenutoFile => ({
  tipo: 'file',
  valore,
  mimeType,
  nomeFile,
});

// ——— Contenuti per chiave ————————————————————————————————————————————

export interface HomeTestiContenuto {
  homepageTitle: ContenutoTesto;
  homepageSubtitle: ContenutoTesto;
  cosaEGiornaliDelPiemonte: ContenutoHtml;
  cosaOffre: ContenutoHtml;
  tab1Title: ContenutoHtml;
  tab2Title: ContenutoHtml;
}

export interface HomeCosaPuoiFareItem {
  titolo: ContenutoTesto;
  desc: ContenutoTesto;
}

export interface HomeCosaPuoiFareContenuto {
  items: HomeCosaPuoiFareItem[];
}

// Il video di presentazione può essere un link esterno (es. YouTube) oggi,
// ma domani potrebbe diventare un file caricato direttamente (base64): il FE
// deve quindi gestire entrambi i tipi, distinguendoli tramite `tipo`.
export interface HomeVideoContenuto {
  video: ContenutoLink | ContenutoFile;
}

export interface FooterLinkItem {
  label: ContenutoTesto;
  url: ContenutoLink;
}

export interface FooterLinksContenuto {
  links: FooterLinkItem[];
}

export interface FooterEnteItem {
  nome: ContenutoTesto;
  logo: ContenutoImmagine;
  website: ContenutoLink;
  indirizzo: ContenutoTesto;
  email: ContenutoTesto;
}

export interface FooterEntiContenuto {
  enti: FooterEnteItem[];
}

export interface ProgettoTestiContenuto {
  cosaEGiornaliDelPiemonte: ContenutoHtml;
  chiRealizzaIlProgetto: ContenutoHtml;
  origini: ContenutoHtml;
  obiettivi: ContenutoHtml;
  comeUsareLaPiattaforma: ContenutoHtml;
}

export interface FascicoloTestiContenuto {
  cosaEIlFascicolo: ContenutoHtml;
  stepAvviaRicerca: ContenutoHtml;
  stepAggiungiAlFascicolo: ContenutoHtml;
  stepVisualizzaFascicolo: ContenutoHtml;
  stepScaricaPdf: ContenutoHtml;
  importanteCookie: ContenutoHtml;
  importanteAvvisoPerdita: ContenutoHtml;
  importanteElencoCasi: ContenutoHtml;
}

export interface LineeGuidaTestiContenuto {
  descrizioneSezione: ContenutoHtml;
}

export interface LineeGuidaFileContenuto {
  lineeGuida: ContenutoFile;
  allegatiTecnici: ContenutoFile;
}

// ——— Registro centrale: chiave -> forma del contenuto ————————————————
// Unico punto in cui è dichiarata la struttura di ogni contenuto dinamico.
// `ContenutoStaticoService.getByKey` è tipizzato su questa mappa: ogni nuovo
// consumer ottiene subito l'autocompletamento/type-check corretto senza dover
// ridefinire un'interfaccia locale per ciascuna chiave.
export interface ContenutiDinamiciMap {
  HOME_TESTI: HomeTestiContenuto;
  HOME_COSA_PUOI_FARE: HomeCosaPuoiFareContenuto;
  HOME_VIDEO: HomeVideoContenuto;
  FOOTER_LINKS: FooterLinksContenuto;
  FOOTER_ENTI: FooterEntiContenuto;
  PROGETTO_TESTI: ProgettoTestiContenuto;
  FASCICOLO_TESTI: FascicoloTestiContenuto;
  LINEE_GUIDA_TESTI: LineeGuidaTestiContenuto;
  LINEE_GUIDA_FILE: LineeGuidaFileContenuto;
}

export const CONTENUTI_DINAMICI_KEYS: { [K in keyof ContenutiDinamiciMap]: K } = {
  HOME_TESTI: 'HOME_TESTI',
  HOME_COSA_PUOI_FARE: 'HOME_COSA_PUOI_FARE',
  HOME_VIDEO: 'HOME_VIDEO',
  FOOTER_LINKS: 'FOOTER_LINKS',
  FOOTER_ENTI: 'FOOTER_ENTI',
  PROGETTO_TESTI: 'PROGETTO_TESTI',
  FASCICOLO_TESTI: 'FASCICOLO_TESTI',
  LINEE_GUIDA_TESTI: 'LINEE_GUIDA_TESTI',
  LINEE_GUIDA_FILE: 'LINEE_GUIDA_FILE',
};
